import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { ChartContainer, type ChartConfig } from "@/shared/components/ui/chart"
import { getAHTDistribution } from "@/shared/api/statistics"

type AHTDatum = {
  rangeKey: string
  percentage: number
}

const RANGE_ORDER: string[] = [
  "under1",
  "1to2",
  "2to3",
  "3to4",
  "4to5",
  "5to6",
  "6to8",
  "8to10",
  "10to15",
  "15to20",
  "20plus",
]

export function AHTDistributionChart() {
  const { t } = useTranslation()
  const [chartState, setChartState] = useState<{
    data: AHTDatum[]
    hasData: boolean
  }>(() => ({
    data: RANGE_ORDER.map((rangeKey) => ({ rangeKey, percentage: 0 })),
    hasData: false,
  }))

  useEffect(() => {
    const fetchData = async () => {
      try {
        const distribution = await getAHTDistribution()

        if (distribution && distribution.length > 0) {
          const distributionMap = new Map(
            distribution.map((item) => [item.rangeKey, item.percentage]),
          )
          const orderedData = RANGE_ORDER.map((rangeKey) => ({
            rangeKey,
            percentage: distributionMap.get(rangeKey) ?? 0,
          }))
          const hasData = orderedData.some((item) => item.percentage > 0)

          setChartState({
            data: orderedData,
            hasData,
          })
        } else {
          setChartState((previous) => ({
            ...previous,
            hasData: false,
          }))
        }
      } catch (error) {
        console.error("Error fetching AHT distribution:", error)
        // Keep fallback data on error
      }
    }

    fetchData()
  }, [])

  const { data: rawData, hasData } = chartState

  const chartConfig: ChartConfig = {
    percentage: {
      label: t("statistics.aht.series.percentage"),
      color: "hsl(var(--chart-1))",
    },
  }

  const chartData = hasData
    ? rawData.map((item) => ({
        ...item,
        timeRange: t(`statistics.aht.ranges.${item.rangeKey}`),
      }))
    : []

  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <ChartContainer
        config={chartConfig}
        className="h-[350px] min-w-[320px] w-full"
        empty={hasData ? undefined : t("statistics.noData")}
      >
        {hasData && (
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{
              left: 0,
              right: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
            <XAxis
              type="number"
              dataKey="percentage"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              stroke="hsl(var(--muted-foreground))"
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis
              type="category"
              dataKey="timeRange"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              stroke="hsl(var(--muted-foreground))"
              width={85}
              tick={{ fontSize: 12 }}
            />
            <Bar dataKey="percentage" fill="var(--color-percentage)" radius={5} />
          </BarChart>
        )}
      </ChartContainer>
    </div>
  )
}
